import type { Lead, Turma } from '@/mocks/admin';

interface TurmaDetalheModalProps {
  turma: Turma & { leads: Lead[] };
  onClose: () => void;
  onVerLead: (lead: Lead) => void;
}

const statusConfig: Record<Lead['status'], { label: string; color: string; bg: string }> = {
  novo: { label: 'Novo', color: 'text-gray-600', bg: 'bg-gray-100' },
  contatado: { label: 'Contatado', color: 'text-amber-700', bg: 'bg-amber-50' },
  interessado: { label: 'Interessado', color: 'text-amber-700', bg: 'bg-amber-50' },
  confirmado: { label: 'Confirmado', color: 'text-emerald-700', bg: 'bg-emerald-50' },
  desistiu: { label: 'Desistiu', color: 'text-red-600', bg: 'bg-red-50' },
};

const nivelConfig: Record<number, { label: string; color: string; bg: string; bar: string }> = {
  1: { label: 'Nível 1 — Em Risco', color: 'text-red-600', bg: 'bg-red-50', bar: 'bg-red-500' },
  2: { label: 'Nível 2 — Desorganizado', color: 'text-orange-600', bg: 'bg-orange-50', bar: 'bg-orange-500' },
  3: { label: 'Nível 3 — Estruturando', color: 'text-amber-600', bg: 'bg-amber-50', bar: 'bg-amber-500' },
  4: { label: 'Nível 4 — Escalável', color: 'text-emerald-600', bg: 'bg-emerald-50', bar: 'bg-emerald-500' },
};

const statusTurmaConfig: Record<string, { label: string; color: string; bg: string; dot: string }> = {
  em_formacao: { label: 'Em formação', color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200', dot: 'bg-amber-400' },
  completa: { label: 'Completa', color: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-200', dot: 'bg-emerald-400' },
  encerrada: { label: 'Encerrada', color: 'text-gray-400', bg: 'bg-gray-100 border-gray-200', dot: 'bg-gray-400' },
};

export default function TurmaDetalheModal({ turma, onClose, onVerLead }: TurmaDetalheModalProps) {
  const cfg = statusTurmaConfig[turma.status];
  const ocupacao = turma.leads.length;
  const pct = Math.round((ocupacao / turma.maxVagas) * 100);
  const vagasRestantes = turma.maxVagas - ocupacao;
  const confirmados = turma.leads.filter((l) => l.status === 'confirmado').length;

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl z-10">
          <div>
            <h2 className="text-gray-900 font-black text-lg">{turma.nome}</h2>
            <p className="text-gray-400 text-xs mt-0.5">
              {formatDate(turma.dataInicio)} → {formatDate(turma.dataFim)}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full border ${cfg.bg} ${cfg.color}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
              {cfg.label}
            </span>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-gray-500" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Progresso */}
          <div className="bg-gray-50 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-gray-600 text-sm font-bold">Preenchimento da turma</span>
              <span className="text-gray-700 text-sm font-black">{ocupacao}/{turma.maxVagas} vagas</span>
            </div>
            <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden mb-2">
              <div
                className={`h-full rounded-full transition-all duration-700 ${pct >= 100 ? 'bg-emerald-500' : pct >= 70 ? 'bg-amber-500' : 'bg-gray-400'}`}
                style={{ width: `${Math.min(pct, 100)}%` }}
              />
            </div>
            <div className="grid grid-cols-3 gap-3 mt-4">
              <div className="text-center bg-white rounded-xl p-3">
                <p className="text-gray-900 font-black text-2xl">{ocupacao}</p>
                <p className="text-gray-400 text-xs">Leads</p>
              </div>
              <div className="text-center bg-white rounded-xl p-3">
                <p className="text-emerald-600 font-black text-2xl">{confirmados}</p>
                <p className="text-gray-400 text-xs">Confirmados</p>
              </div>
              <div className="text-center bg-white rounded-xl p-3">
                <p className="text-amber-600 font-black text-2xl">{vagasRestantes}</p>
                <p className="text-gray-400 text-xs">Restantes</p>
              </div>
            </div>
          </div>

          {/* Lista de mentorados */}
          <div>
            <h3 className="text-gray-500 text-xs font-bold uppercase tracking-wider mb-3">
              Mentorados ({turma.leads.length})
            </h3>

            {turma.leads.length === 0 ? (
              <div className="bg-gray-50 rounded-2xl p-8 text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-gray-100 mx-auto mb-3">
                  <i className="ri-user-add-line text-gray-400 text-xl" />
                </div>
                <p className="text-gray-400 text-sm font-medium">Nenhum lead alocado nesta turma ainda</p>
                <p className="text-gray-300 text-xs mt-1">Aloque leads confirmados na aba Gestão de Leads</p>
              </div>
            ) : (
              <div className="space-y-3">
                {turma.leads.map((lead, idx) => {
                  const statusCfg = statusConfig[lead.status];
                  const nivelCfg = lead.diagnostico ? nivelConfig[lead.diagnostico.nivelNumero] : null;

                  return (
                    <div
                      key={lead.id}
                      className="flex items-center gap-4 p-4 bg-gray-50 rounded-2xl hover:bg-gray-100 transition-colors"
                    >
                      {/* Número */}
                      <div className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-200 flex-shrink-0">
                        <span className="text-gray-500 text-xs font-black">{idx + 1}</span>
                      </div>

                      {/* Avatar */}
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white border border-gray-200 flex-shrink-0">
                        <span className="text-gray-700 text-sm font-black">{lead.nome.charAt(0)}</span>
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <p className="text-gray-900 text-sm font-bold truncate">{lead.nome}</p>
                        <p className="text-gray-400 text-xs truncate">{lead.empresa}</p>
                      </div>

                      {/* Nível */}
                      {nivelCfg && (
                        <div className="flex flex-col items-end gap-1">
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${nivelCfg.bg} ${nivelCfg.color}`}>
                            N{lead.diagnostico!.nivelNumero}
                          </span>
                          <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${nivelCfg.bar}`}
                              style={{ width: `${lead.diagnostico!.percentualGeral}%` }}
                            />
                          </div>
                        </div>
                      )}

                      {/* Status */}
                      <span className={`text-xs font-bold px-2.5 py-1 rounded-full whitespace-nowrap ${statusCfg.bg} ${statusCfg.color}`}>
                        {statusCfg.label}
                      </span>

                      {/* Ver detalhes */}
                      <button
                        onClick={() => onVerLead(lead)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white transition-colors cursor-pointer flex-shrink-0"
                        title="Ver detalhes do lead"
                      >
                        <i className="ri-eye-line text-gray-400 text-sm" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
